import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Vite-resolved URL of the pdf.js worker. Required by react-pdf >= 7.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface PdfViewerProps {
  src: string;
}

const FILE_OPTIONS = {
  cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/cmaps/',
  cMapPacked: true
};

const PdfViewer = ({ src }: PdfViewerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const node = containerRef.current;

    const update = () => setContainerWidth(node.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const onLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setPageCount(numPages);
    setHasError(false);
  }, []);

  const onLoadError = useCallback(() => {
    setHasError(true);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#F1F3F6',
        WebkitOverflowScrolling: 'touch'
      }}>
      <Document
        file={src}
        onLoadSuccess={onLoadSuccess}
        onLoadError={onLoadError}
        options={FILE_OPTIONS}
        loading={
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 240
            }}>
            <CircularProgress size={48} />
          </Box>
        }
        error={
          <Box style={{ padding: 24, textAlign: 'center', color: '#5D7083' }}>
            Couldn’t load this quote.
          </Box>
        }>
        {!hasError &&
          containerWidth > 0 &&
          Array.from({ length: pageCount }, (_, idx) => (
            <Box
              key={`page-${idx + 1}`}
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 12
              }}>
              <Page
                pageNumber={idx + 1}
                width={containerWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Box>
          ))}
      </Document>
    </div>
  );
};

export default memo(PdfViewer);
